import React, { useState, useRef, useEffect } from 'react';
import { Palette, Tag, Grid as GridIcon, LayoutGrid, X } from 'lucide-react';
import { ChartConfig } from '../../types';

interface EncodingBarProps {
    config: ChartConfig;
    onUpdateConfig: (updates: Partial<ChartConfig>) => void;
    allCols: { label: string; value: string; isNumeric?: boolean }[];
}

// Simple Popover component
const EncodingPopover: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    anchorEl: HTMLElement | null;
}> = ({ isOpen, onClose, children, anchorEl }) => {
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
                anchorEl && !anchorEl.contains(e.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, anchorEl]);

    if (!isOpen || !anchorEl) return null;

    const rect = anchorEl.getBoundingClientRect();
    const parentRect = anchorEl.closest('.border-y')?.getBoundingClientRect();

    // Calculate position relative to parent container
    const top = parentRect ? rect.bottom - parentRect.top + 8 : rect.bottom + 8;
    const left = parentRect ? rect.left - parentRect.left : rect.left;

    return (
        <div
            ref={popoverRef}
            className="absolute z-50 bg-obsidian-900 border border-gold-500/30 rounded-lg shadow-2xl p-3 min-w-[200px] max-h-[300px] overflow-y-auto"
            style={{
                top: `${top}px`,
                left: `${left}px`,
            }}
        >
            {children}
        </div>
    );
};

export const EncodingBar: React.FC<EncodingBarProps> = ({
    config,
    onUpdateConfig,
    allCols
}) => {
    const { encoding = {}, facets } = config;
    const [activePopover, setActivePopover] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const categoricalCols = allCols.filter(c => !c.isNumeric);

    const hasColorBy = !!encoding?.color?.field;
    const hasFacets = !!facets?.field;

    const openPopover = (name: string, el: HTMLElement) => {
        setActivePopover(name);
        setAnchorEl(el);
    };

    const closePopover = () => {
        setActivePopover(null);
        setAnchorEl(null);
    };

    const getFieldLabel = (value?: string) => {
        if (!value) return 'None';
        const field = allCols.find(c => c.value === value);
        return field?.label.split('.').pop() || value;
    };

    return (
        <div className="border-y border-white/10 bg-black/20 p-3 relative">
            <div className="flex items-center gap-2 flex-wrap">
                {/* Color By Button */}
                <button
                    onClick={(e) => openPopover('color', e.currentTarget)}
                    disabled={hasFacets}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-xs ${hasColorBy
                        ? 'bg-gold-500/20 border-gold-500/50 text-gold-400'
                        : 'bg-white/5 border-white/20 text-gray-400 hover:border-gold-500/30'
                        } ${hasFacets ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Palette className="w-3.5 h-3.5" />
                    <span className="font-medium">{hasColorBy ? getFieldLabel(encoding?.color?.field) : 'Color'}</span>
                </button>

                {/* Label By Button */}
                <button
                    onClick={(e) => openPopover('label', e.currentTarget)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-xs ${encoding?.label?.field
                        ? 'bg-gold-500/20 border-gold-500/50 text-gold-400'
                        : 'bg-white/5 border-white/20 text-gray-400 hover:border-gold-500/30'
                        }`}
                >
                    <Tag className="w-3.5 h-3.5" />
                    <span className="font-medium">{encoding?.label?.field ? getFieldLabel(encoding?.label?.field) : 'Label'}</span>
                </button>

                {/* Detail By Button */}
                <button
                    onClick={(e) => openPopover('detail', e.currentTarget)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-xs ${encoding?.detail?.field
                        ? 'bg-gold-500/20 border-gold-500/50 text-gold-400'
                        : 'bg-white/5 border-white/20 text-gray-400 hover:border-gold-500/30'
                        }`}
                >
                    <GridIcon className="w-3.5 h-3.5" />
                    <span className="font-medium">{encoding?.detail?.field ? getFieldLabel(encoding?.detail?.field) : 'Detail'}</span>
                </button>

                {/* Facets Button */}
                <button
                    onClick={(e) => openPopover('facets', e.currentTarget)}
                    disabled={hasColorBy}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-xs ${hasFacets
                        ? 'bg-gold-500/20 border-gold-500/50 text-gold-400'
                        : 'bg-white/5 border-white/20 text-gray-400 hover:border-gold-500/30'
                        } ${hasColorBy ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    <span className="font-medium">{hasFacets ? getFieldLabel(facets?.field) : 'Facets'}</span>
                </button>
            </div>

            {/* Color By Popover */}
            <EncodingPopover isOpen={activePopover === 'color'} onClose={closePopover} anchorEl={anchorEl}>
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gold-500">COLOR BY</h4>
                        <button onClick={closePopover} className="text-gray-400 hover:text-white">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    {categoricalCols.map(col => (
                        <button
                            key={col.value}
                            onClick={() => {
                                onUpdateConfig({ encoding: { ...encoding, color: { field: col.value } } });
                                closePopover();
                            }}
                            className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-gold-500/20 ${encoding?.color?.field === col.value ? 'bg-gold-500/30 text-gold-400' : 'text-gray-300'
                                }`}
                        >
                            {col.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            onUpdateConfig({ encoding: { ...encoding, color: undefined } });
                            closePopover();
                        }}
                        className="w-full text-left px-2 py-1 rounded text-xs text-gray-500 hover:bg-red-500/20 border-t border-white/10 mt-2 pt-2"
                    >
                        Clear
                    </button>
                </div>
            </EncodingPopover>

            {/* Label By Popover */}
            <EncodingPopover isOpen={activePopover === 'label'} onClose={closePopover} anchorEl={anchorEl}>
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gold-500">LABEL BY</h4>
                        <button onClick={closePopover} className="text-gray-400 hover:text-white">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    {allCols.map(col => (
                        <button
                            key={col.value}
                            onClick={() => {
                                onUpdateConfig({ encoding: { ...encoding, label: { field: col.value } } });
                                closePopover();
                            }}
                            className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-gold-500/20 ${encoding?.label?.field === col.value ? 'bg-gold-500/30 text-gold-400' : 'text-gray-300'
                                }`}
                        >
                            {col.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            onUpdateConfig({ encoding: { ...encoding, label: undefined } });
                            closePopover();
                        }}
                        className="w-full text-left px-2 py-1 rounded text-xs text-gray-500 hover:bg-red-500/20 border-t border-white/10 mt-2 pt-2"
                    >
                        Clear
                    </button>
                </div>
            </EncodingPopover>

            {/* Detail By Popover */}
            <EncodingPopover isOpen={activePopover === 'detail'} onClose={closePopover} anchorEl={anchorEl}>
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gold-500">DETAIL BY</h4>
                        <button onClick={closePopover} className="text-gray-400 hover:text-white">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    {categoricalCols.map(col => (
                        <button
                            key={col.value}
                            onClick={() => {
                                onUpdateConfig({ encoding: { ...encoding, detail: { field: col.value } } });
                                closePopover();
                            }}
                            className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-gold-500/20 ${encoding?.detail?.field === col.value ? 'bg-gold-500/30 text-gold-400' : 'text-gray-300'
                                }`}
                        >
                            {col.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            onUpdateConfig({ encoding: { ...encoding, detail: undefined } });
                            closePopover();
                        }}
                        className="w-full text-left px-2 py-1 rounded text-xs text-gray-500 hover:bg-red-500/20 border-t border-white/10 mt-2 pt-2"
                    >
                        Clear
                    </button>
                </div>
            </EncodingPopover>

            {/* Facets Popover */}
            <EncodingPopover isOpen={activePopover === 'facets'} onClose={closePopover} anchorEl={anchorEl}>
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold text-gold-500">FACETS</h4>
                        <button onClick={closePopover} className="text-gray-400 hover:text-white">
                            <X className="w-3 h-3" />
                        </button>
                    </div>
                    {categoricalCols.map(col => (
                        <button
                            key={col.value}
                            onClick={() => {
                                onUpdateConfig({ facets: { field: col.value } });
                                closePopover();
                            }}
                            className={`w-full text-left px-2 py-1 rounded text-xs hover:bg-gold-500/20 ${facets?.field === col.value ? 'bg-gold-500/30 text-gold-400' : 'text-gray-300'
                                }`}
                        >
                            {col.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            onUpdateConfig({ facets: undefined });
                            closePopover();
                        }}
                        className="w-full text-left px-2 py-1 rounded text-xs text-gray-500 hover:bg-red-500/20 border-t border-white/10 mt-2 pt-2"
                    >
                        Clear
                    </button>
                </div>
            </EncodingPopover>
        </div>
    );
};
